import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select } from './ui/select';
import { getQueryFn } from '../lib/queryClient';
import type { Country } from '../types';

// Activity options now come from backend industry equipment list.

const defaultUnits = [
  { value: 'liters', label: 'Liters' },
  { value: 'gallons', label: 'Gallons' },
  { value: 'kg', label: 'Kilograms' },
  { value: 'm3', label: 'Cubic Meters' },
  { value: 'kWh', label: 'kWh (if applicable)' },
  { value: 'Barrel', label: 'Barrel' },
];

export interface Scope1MeasuredFormProps {
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
  industry?: string | null;
  scopeLabel?: 'Scope 1' | 'Scope 2';
  selectedCompanyId?: number;
}

export function Scope1MeasuredForm({ onSubmit, isSubmitting, industry, scopeLabel = 'Scope 1', selectedCompanyId }: Scope1MeasuredFormProps) {
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [emissionFactor, setEmissionFactor] = useState('');
  const [emissionFactorSource, setEmissionFactorSource] = useState('');
  const [notes, setNotes] = useState('');
  const [activityType, setActivityType] = useState('');
  const [emissionGas, setEmissionGas] = useState<'CO2' | 'CH4' | 'N2O'>('CO2');

  // Note: Region state has been removed
  const [country, setCountry] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear());

  // Metadata
  const [standardUnit, setStandardUnit] = useState('');
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [standardQuantity, setStandardQuantity] = useState<number | null>(null);
  const [co2ePreview, setCo2ePreview] = useState<number | null>(null);

  // Industry equipment options for Activity dropdown
  const { data: equipmentRows, error: equipmentError, isLoading: equipmentLoading } = useQuery({
    queryKey: industry && scopeLabel ? [
      `/api/industry-equipment?industry=${encodeURIComponent(industry)}&scope=${encodeURIComponent(scopeLabel)}`
    ] : [null],
    queryFn: getQueryFn(),
    enabled: !!industry && !!scopeLabel,
  });

  // Debug logging
  console.log('Scope1MeasuredForm Debug:', {
    industry,
    scopeLabel,
    equipmentRows,
    equipmentError,
    equipmentLoading,
    queryEnabled: !!industry && !!scopeLabel
  });

  // Fetch company countries
  const { data: companyCountries } = useQuery<Country[]>({
    queryKey: selectedCompanyId ? [`/api/countries/company/${selectedCompanyId}`] : [null],
    queryFn: getQueryFn(),
    enabled: !!selectedCompanyId,
  });


  interface ActivityOption { value: string; label: string; unit?: string }
  const activityOptions: ActivityOption[] = React.useMemo(() => {
    return (equipmentRows || []).map((r: any) => ({ value: r.equipment_name as string, label: r.equipment_name as string, unit: r.unit as string | undefined }));
  }, [equipmentRows]);

  // Default activity and unit when options load
  useEffect(() => {
    if (!activityType && activityOptions.length > 0) {
      setActivityType(activityOptions[0].value);
      if (activityOptions[0].unit) setUnit(activityOptions[0].unit);
    }
  }, [activityOptions, activityType]);

  // Note: Country filtering logic has been removed

  // Note: Region change effect has been removed

  // Fetch available fuels/units from new metadata endpoint
  const { data: fuelsList } = useQuery({
    queryKey: [`/api/scope1/fuels`],
    queryFn: getQueryFn(),
  });

  // Auto-fetch fuel meta when fuelType or unit changes
  const { data: fuelMeta } = useQuery({
    queryKey: fuelType && unit ? [
      `/api/scope1/fuel-meta?fuel_type=${encodeURIComponent(fuelType)}&unit=${encodeURIComponent(unit)}`
    ] : [null],
    queryFn: getQueryFn(),
    enabled: !!fuelType && !!unit,
  });

  // Initialize unit from default when selecting a fuel
  useEffect(() => {
    if (fuelType && fuelsList) {
      const found = (fuelsList as any[]).find(f => f.fuel_type === fuelType);
      if (found?.default_unit) {
        setUnit(found.default_unit);
      } else if (found?.units?.length) {
        setUnit(found.units[0]);
      }
    }
  }, [fuelType, fuelsList]);

  // When meta arrives, set factor, source, standard unit and conversion
  useEffect(() => {
    if (fuelMeta) {
      setEmissionFactor(String(fuelMeta.emission_factor ?? ''));
      setEmissionFactorSource(fuelMeta.emission_factor_source ?? '');
      setStandardUnit(fuelMeta.standard_unit ?? '');
      setConversionRate(fuelMeta.conversion_rate ?? 1);
    }
  }, [fuelMeta]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const qty = parseFloat(quantity);
    const ef = parseFloat(emissionFactor);
    const convQty = isFiniteNumber(qty) ? qty * (conversionRate || 1) : 0;
    const computedCO2e = isFiniteNumber(convQty) && isFiniteNumber(ef) ? convQty * ef : undefined;

    onSubmit({
      scope: 'scope1',
      activity_type: fuelType,
      activity_description: activityType || undefined,
      quantity: qty,
      unit: unit,
      emission_factor: ef,
      emission_factor_source: emissionFactorSource || undefined,
      is_estimated: false,
      notes: (notes ? `${notes} ` : '') + `(Gas: ${emissionGas})`,
      // Note: region has been removed
      country,
      reporting_year: reportingYear,
      // server will recompute and persist normalized rows
      co2_equivalent: computedCO2e,
      standard_value: convQty,
    });
  };

  // Live preview of Standard Quantity and CO2e
  useEffect(() => {
    const qty = parseFloat(quantity);
    const ef = parseFloat(emissionFactor);
    const convQty = isFiniteNumber(qty) ? qty * (conversionRate || 1) : NaN;
    if (isFiniteNumber(convQty)) {
      setStandardQuantity(convQty);
    } else {
      setStandardQuantity(null);
    }
    if (isFiniteNumber(convQty) && isFiniteNumber(ef)) {
      setCo2ePreview(convQty * ef);
    } else {
      setCo2ePreview(null);
    }
  }, [quantity, emissionFactor, conversionRate]);

  function isFiniteNumber(n: any) {
    return typeof n === 'number' && isFinite(n);
  }

  function normalizeUnit(u?: string) {
    if (!u) return '';
    return u.replace('m³', 'm3');
  }

  const fuelOptions = useMemo(() => {
    return (fuelsList || [])
      .map((f: any) => ({ value: f.fuel_type as string, label: f.fuel_type as string }));
  }, [fuelsList]);

  const unitOptions = useMemo(() => {
    const found = (fuelsList as any[] | undefined)?.find(f => f.fuel_type === fuelType);
    const units: string[] = found?.units || [];
    const opts = units.map(u => ({ value: u, label: u }));
    if (unit && !opts.some(o => o.value === unit)) opts.unshift({ value: unit, label: unit });
    return [{ value: '', label: 'Select unit…' }, ...opts];
  }, [fuelsList, fuelType, unit]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Row 1: Country + Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Country"
            value={country}
            options={[
              { value: '', label: 'Select country…' },
              ...(companyCountries || []).map((c: Country) => ({ value: c.code, label: c.name }))
            ]}
            onChange={(e: any) => setCountry(e?.target ? e.target.value : e)}
          />
          <Select
            label="Activity (optional)"
            value={activityType}
            options={[{ value: '', label: 'Select activity…' }, ...activityOptions]}
            onChange={(e: any) => {
            const val = e?.target ? e.target.value : e;
            setActivityType(val);
            const selected = activityOptions.find(o => o.value === val);
            if (selected?.unit) setUnit(selected.unit);
          }}
        />
        </div>

      {/* Row 2: Fuel + Unit */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Fuel type"
          value={fuelType}
          options={[{ value: '', label: 'Select fuel…' }, ...fuelOptions]}
          onChange={(e: any) => setFuelType(e?.target ? e.target.value : e)}
        />
        <Select
          label="Unit"
          value={unit}
          options={unitOptions}
          onChange={(e: any) => setUnit(e?.target ? e.target.value : e)}
        />
      </div>

      {/* Row 3: Quantity + Emission Type (gas) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Quantity"
          placeholder="Quantity"
          type="number"
          step="0.0001"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
        />
        <Select
          label="Emission Type (gas)"
          value={emissionGas}
          options={[
            { value: 'CO2', label: 'CO2 (Carbon Dioxide)' },
            { value: 'CH4', label: 'CH4 (Methane)' },
            { value: 'N2O', label: 'N2O (Nitrous Oxide)' },
          ]}
          onChange={(e: any) => setEmissionGas((e?.target ? e.target.value : e) as any)}
        />
      </div>

      {/* Row 4: Reporting period start + end */}
      <div>
        <Input
          label="Reporting Year"
          type="number"
          min="2000"
          max="2100"
          value={reportingYear}
          onChange={(e) => setReportingYear(parseInt(e.target.value) || new Date().getFullYear())}
          required
        />
      </div>

      {/* Auto-calculated details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label={`Emission factor (kg ${emissionGas}e per standard unit)`}
          placeholder="Auto-fetched"
          type="number"
          step="0.0001"
          value={emissionFactor}
          onChange={(e) => setEmissionFactor(e.target.value)}
          required
        />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Conversion rate</label>
          <div className="text-sm text-gray-600 border border-dashed rounded-md p-2">
            {Number.isFinite(conversionRate) ? conversionRate.toFixed(6) : '—'}
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Standard Quantity (Quantity × conversion)</label>
          <div className="text-sm text-gray-600 border border-dashed rounded-md p-2">
            {standardQuantity !== null ? `${standardQuantity.toFixed(4)}` : '—'}
          </div>
        </div>
      </div>

      {emissionFactorSource && (
        <Input
          label="Emission factor source (optional)"
          placeholder="Emission factor source (optional)"
          value={emissionFactorSource}
          onChange={(e) => setEmissionFactorSource(e.target.value)}
        />
      )}

      {co2ePreview !== null && (
        <div className="text-lg font-bold text-green-800 bg-green-50 px-4 py-3 rounded-lg border-2 border-green-200">
          Preview {emissionGas}e: {co2ePreview.toFixed(4)} kg {emissionGas}e
        </div>
      )}
      <Input label="Notes (optional)" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Submitting…' : 'Save Measured Entry'}</Button>
      </div>
    </form>
  );
}
